import React, { useEffect, useMemo, useState } from 'react'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'
import { useSelector } from 'react-redux'
import MaskedInput from '../../../components/Template/Input/MaskedInput'
import { getRegs, insert, oikosGet, update } from '../../../utils/fc'
import { notifyError, notifySuccess } from '../../../utils/notify'
import './RegistrarPonto.css'

const STATUS_NAO_INICIADO = 1
const STATUS_INICIADO = 2
const STATUS_CONCLUIDO = 3

function listaDe(resposta) {
  const corpo = resposta && resposta.data
  return Array.isArray(corpo) ? corpo : []
}

function mesmoId(a, b) {
  return String(a) === String(b)
}

function minutosDe(valor) {
  if (valor == null || valor === '') return 0
  const texto = String(valor).trim()
  const partes = texto.match(/^(\d+):(\d{2})(?::(\d{2}))?/)
  if (!partes) return 0
  return (Number(partes[1]) * 60) + Number(partes[2])
}

function tempoSql(minutos) {
  const horas = Math.floor(minutos / 60)
  const resto = minutos % 60
  return `${String(horas).padStart(2, '0')}:${String(resto).padStart(2, '0')}:00`
}

function rotuloHoras(minutos) {
  const horas = Math.floor(minutos / 60)
  const resto = minutos % 60
  const unidadeHora = horas === 1 ? 'hora' : 'horas'
  if (!resto) return `${horas} ${unidadeHora}`
  const unidadeMinuto = resto === 1 ? 'minuto' : 'minutos'
  return `${horas} ${unidadeHora} e ${resto} ${unidadeMinuto}`
}

function rotuloAtividades(quantidade) {
  return `${quantidade} ${quantidade === 1 ? 'atividade' : 'atividades'}`
}

function tempoPreenchido(texto) {
  return /^(\d{2}):([0-5]\d)$/.test(String(texto || '')) && texto !== '00:00'
}

function agoraSql() {
  const data = new Date()
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  const hora = String(data.getHours()).padStart(2, '0')
  const minuto = String(data.getMinutes()).padStart(2, '0')
  const segundo = String(data.getSeconds()).padStart(2, '0')
  return `${data.getFullYear()}-${mes}-${dia} ${hora}:${minuto}:${segundo}`
}

function hojeSql() {
  return agoraSql().slice(0, 10)
}

function ontemSql() {
  const data = new Date()
  data.setDate(data.getDate() - 1)
  const mes = String(data.getMonth() + 1).padStart(2, '0')
  const dia = String(data.getDate()).padStart(2, '0')
  return `${data.getFullYear()}-${mes}-${dia}`
}

function setorIdDaNatureza(item, naturezas) {
  if (item == null || item.setorAtiv_id == null || item.setorAtiv_id === '') return null
  const natureza = naturezas.find(opcao => mesmoId(opcao.setorAtiv_id, item.setorAtiv_id))
  if (!natureza || natureza.setor_id == null || natureza.setor_id === '') return null
  const setorId = Number(natureza.setor_id)
  return Number.isNaN(setorId) ? null : setorId
}

export default function RegistrarPonto({ embutido, onRegistrado, retroativo }) {
  const pfId = useSelector(state => state.auth.user && state.auth.user.pf_id)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState('')
  const [atividades, setAtividades] = useState([])
  const [statusLista, setStatusLista] = useState([])
  const [selecao, setSelecao] = useState({})
  const [tempos, setTempos] = useState({})
  const [concluir, setConcluir] = useState({})
  const [gravando, setGravando] = useState(false)
  const [dataRetro, setDataRetro] = useState('')
  const [justificativa, setJustificativa] = useState('')

  useEffect(() => {
    let vivo = true
    async function carregar() {
      setCarregando(true)
      setErro('')
      if (!pfId) {
        if (vivo) {
          setErro('Não foi possível identificar o colaborador.')
          setCarregando(false)
        }
        return
      }
      try {
        const [lista, statusAtiv, naturezaAtiv] = await Promise.all([
          getRegs('atividade', { pf_id: Number(pfId) }),
          oikosGet({
            action: 'get',
            table: 'atividadeStatus',
            data: { atividadeStatus_ativo: 1, atividadeStatus_exibir: 1 },
          }),
          oikosGet({ action: 'get', table: 'setorAtiv', data: {} }),
        ])
        if (!vivo) return
        if (!Array.isArray(lista)) throw new Error('carga')
        const status = listaDe(statusAtiv)
        const naturezas = listaDe(naturezaAtiv)
        setStatusLista(status)
        setAtividades(lista
          .filter(item => {
            if (Number(item.atividade_ativo) === 0) return false
            if (embutido) return Number(item.atividadeStatus_id) === STATUS_INICIADO
            return Number(item.atividadeStatus_id) !== STATUS_CONCLUIDO
          })
          .map(item => {
            const setorId = setorIdDaNatureza(item, naturezas)
            return setorId == null ? null : { ...item, setor_id: setorId }
          })
          .filter(Boolean))
      } catch (e) {
        if (!vivo) return
        setErro('Não foi possível carregar as atividades.')
        notifyError('Não foi possível carregar as atividades.')
      } finally {
        if (vivo) setCarregando(false)
      }
    }
    carregar()
    return () => { vivo = false }
  }, [pfId, embutido])

  const ordenadas = useMemo(() => [...atividades].sort((a, b) => {
    const ordem = String(a.atividade_ord || '').localeCompare(String(b.atividade_ord || ''))
    if (ordem) return ordem
    return String(a.atividade_nome || '').localeCompare(String(b.atividade_nome || ''), 'pt-BR')
  }), [atividades])

  const escolhidas = useMemo(
    () => ordenadas.filter(item => selecao[String(item.atividade_id)]),
    [ordenadas, selecao],
  )

  const minutosEstimados = escolhidas.reduce(
    (soma, item) => soma + minutosDe(item.atividade_tempoEstimado),
    0,
  )
  const minutosRealizados = escolhidas.reduce(
    (soma, item) => soma + minutosDe(tempos[String(item.atividade_id)]),
    0,
  )
  const temSelecao = escolhidas.length > 0

  const colunas = useMemo(() => {
    const base = [
      {
        accessorKey: 'atividade_nome',
        header: 'Atividade',
        size: 280,
        Cell: ({ row }) => row.original.atividade_nome || 'Sem nome',
      },
      {
        accessorKey: 'atividade_tempoEstimado',
        header: 'Tempo estimado',
        size: 140,
        Cell: ({ row }) => {
          const minutos = minutosDe(row.original.atividade_tempoEstimado)
          return minutos ? tempoSql(minutos).slice(0, 5) : '00:00'
        },
      },
      {
        id: 'status',
        header: 'Status',
        size: 140,
        Cell: ({ row }) => {
          const status = statusLista.find(item => mesmoId(item.atividadeStatus_id, row.original.atividadeStatus_id))
          return status ? status.atividadeStatus_nome : ''
        },
      },
    ]
    if (!temSelecao) return base
    return [
      ...base,
      {
        id: 'tempoRealizado',
        header: 'Tempo realizado',
        size: 160,
        Cell: ({ row }) => {
          const id = String(row.original.atividade_id)
          if (!selecao[id]) return null
          return (
            <label className="registrar-ponto-celula" htmlFor={`tempo-${id}`}>
              <span className="sr-only">Tempo realizado</span>
              <MaskedInput
                id={`tempo-${id}`}
                mask="00:00"
                lazy={false}
                placeholder="HH:mm"
                value={tempos[id] || ''}
                onClick={event => event.stopPropagation()}
                onMouseDown={event => event.stopPropagation()}
                onAccept={valor => {
                  const texto = String(valor || '')
                  if (texto && texto.length < 5) return
                  setTempos(atual => ({ ...atual, [id]: texto }))
                }}
              />
            </label>
          )
        },
      },
      {
        id: 'tarefaConcluida',
        header: 'Tarefa concluída',
        size: 150,
        Cell: ({ row }) => {
          const id = String(row.original.atividade_id)
          if (!selecao[id]) return null
          return (
            <label className="registrar-ponto-celula" htmlFor={`concluir-${id}`}>
              <span className="sr-only">Tarefa concluída</span>
              <input
                id={`concluir-${id}`}
                type="checkbox"
                checked={Boolean(concluir[id])}
                onClick={event => event.stopPropagation()}
                onMouseDown={event => event.stopPropagation()}
                onChange={event => setConcluir(atual => ({ ...atual, [id]: event.target.checked }))}
              />
            </label>
          )
        },
      },
    ]
  }, [statusLista, temSelecao, selecao, tempos, concluir])

  useEffect(() => {
    const ids = Object.keys(selecao).filter(id => selecao[id])
    const conjunto = new Set(ids)
    setTempos(atual => {
      const proximo = {}
      ids.forEach(id => { proximo[id] = atual[id] || '' })
      const iguais = Object.keys(atual).length === ids.length && ids.every(id => Object.prototype.hasOwnProperty.call(atual, id))
      return iguais ? atual : proximo
    })
    setConcluir(atual => {
      const proximo = {}
      ids.forEach(id => {
        proximo[id] = Object.prototype.hasOwnProperty.call(atual, id) ? atual[id] : false
      })
      const iguais = Object.keys(atual).length === ids.length && ids.every(id => conjunto.has(id))
      return iguais ? atual : proximo
    })
  }, [selecao])

  function aoSelecionar(updater) {
    setSelecao(atual => (typeof updater === 'function' ? updater(atual) : updater))
  }

  async function desfazer(pontoId, pontosAtiv, atividadesAnteriores) {
    if (pontoId) await update('ponto', pontoId, { ponto_ativo: 0 })
    await Promise.all(pontosAtiv.map(id => update('pontoAtiv', id, { pontoAtiv_ativo: 0 })))
    for (let i = 0; i < atividadesAnteriores.length; i += 1) {
      const anterior = atividadesAnteriores[i]
      // eslint-disable-next-line no-await-in-loop
      await update('atividade', anterior.atividade_id, {
        atividade_tempoReal: anterior.atividade_tempoReal || '00:00:00',
        atividadeStatus_id: Number(anterior.atividadeStatus_id),
      })
    }
  }

  async function registrar() {
    if (gravando) return
    if (!escolhidas.length) {
      notifyError('Escolha ao menos uma atividade.')
      return
    }
    const semTempo = escolhidas.some(item => !tempoPreenchido(tempos[String(item.atividade_id)]))
    const semConclusao = escolhidas.some(item => concluir[String(item.atividade_id)] == null)
    if (semTempo || semConclusao) {
      notifyError('Preencha o tempo realizado (HH:mm) e a tarefa concluída de cada atividade escolhida. 00:00 não conta.')
      return
    }
    if (escolhidas.some(item => item.setor_id == null)) {
      notifyError('Só é possível lançar horas em atividades com Natureza da atividade.')
      return
    }
    const dataPonto = retroativo ? dataRetro : hojeSql()
    const textoJustif = retroativo ? justificativa.trim() : ''
    if (retroativo) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dataPonto)) {
        notifyError('Informe a data do registro retroativo.')
        return
      }
      if (dataPonto >= hojeSql()) {
        notifyError('A data do registro retroativo precisa ser anterior a hoje.')
        return
      }
      if (!textoJustif) {
        notifyError('Informe a justificativa do registro retroativo.')
        return
      }
    }
    setGravando(true)
    const pontoId = await insert('ponto', {
      pf_id: Number(pfId),
      ponto_justif: textoJustif,
      ponto_ativo: 1,
    })
    if (!pontoId) {
      notifyError('Não foi possível registrar as atividades.')
      setGravando(false)
      return
    }
    const pontosAtiv = []
    for (let i = 0; i < escolhidas.length; i += 1) {
      const item = escolhidas[i]
      const id = String(item.atividade_id)
      // eslint-disable-next-line no-await-in-loop
      const pontoAtivId = await insert('pontoAtiv', {
        ponto_id: Number(pontoId),
        pf_id: Number(pfId),
        pontoAtiv_ativ: item.atividade_nome || '',
        pontoAtiv_desc: item.atividade_nome || '',
        pontoAtiv_tempo: `${tempos[id]}:00`,
        pontoAtiv_aprov: 0,
        Lancado: 0,
        pontoAtiv_dt: dataPonto,
        pontoAtiv_justif: textoJustif,
        pontoAtiv_ativo: 1,
        pontoAtiv_usuarioMod: Number(pfId),
        pontoAtiv_usuarioCria: Number(pfId),
        atividade_id: Number(item.atividade_id),
        proj_tabela: 'setor',
        proj_id: Number(item.setor_id),
      })
      if (!pontoAtivId) {
        await desfazer(pontoId, pontosAtiv, [])
        notifyError('Não foi possível registrar as atividades.')
        setGravando(false)
        return
      }
      pontosAtiv.push(pontoAtivId)
    }
    const atualizadas = []
    const anteriores = []
    for (let i = 0; i < escolhidas.length; i += 1) {
      const item = escolhidas[i]
      const id = String(item.atividade_id)
      const patch = {
        atividade_tempoReal: tempoSql(minutosDe(item.atividade_tempoReal) + minutosDe(tempos[id])),
      }
      if (concluir[id]) patch.atividadeStatus_id = STATUS_CONCLUIDO
      else if (Number(item.atividadeStatus_id) === STATUS_NAO_INICIADO) patch.atividadeStatus_id = STATUS_INICIADO
      // eslint-disable-next-line no-await-in-loop
      const gravado = await update('atividade', item.atividade_id, patch)
      if (!gravado) {
        await desfazer(pontoId, pontosAtiv, anteriores)
        notifyError('Não foi possível registrar as atividades.')
        setGravando(false)
        return
      }
      anteriores.push(item)
      atualizadas.push({ item, patch })
    }
    setAtividades(lista => lista
      .map(item => {
        const mudanca = atualizadas.find(atual => mesmoId(atual.item.atividade_id, item.atividade_id))
        return mudanca ? { ...item, ...mudanca.patch } : item
      })
      .filter(item => Number(item.atividadeStatus_id) !== STATUS_CONCLUIDO))
    setSelecao({})
    setTempos({})
    setConcluir({})
    setGravando(false)
    notifySuccess(retroativo ? 'Atividades retroativas registradas.' : 'Atividades registradas.')
    if (typeof onRegistrado === 'function') onRegistrado()
  }

  return (
    <div className={embutido ? 'registrar-ponto is-embutido' : 'page-content registrar-ponto'}>
      <section className={`registrar-ponto-resumo${retroativo ? ' is-retro' : ''}`}>
        <header className="registrar-ponto-topo">
          <h1>{retroativo ? 'Registrar atividades retroativas' : 'Registrar atividades'}</h1>
          <p>
            {retroativo
              ? 'Informe a data e a justificativa, selecione as atividades executadas naquele dia e o tempo realizado.'
              : 'Estas são as suas atividades. Selecione as executadas e informe o tempo realizado.'}
          </p>
        </header>
        {retroativo && (
          <>
            <label className="registrar-ponto-campo" htmlFor="registrar-ponto-data">
              Data
              <input
                id="registrar-ponto-data"
                type="date"
                max={ontemSql()}
                value={dataRetro}
                onChange={event => setDataRetro(event.target.value)}
              />
            </label>
            <label className="registrar-ponto-campo" htmlFor="registrar-ponto-justif">
              Justificativa
              <input
                id="registrar-ponto-justif"
                type="text"
                value={justificativa}
                onChange={event => setJustificativa(event.target.value)}
              />
            </label>
          </>
        )}
        <div className="registrar-ponto-painel">
          <h2>Resumo</h2>
          <p>
            {`Tempo estimado: ${rotuloAtividades(escolhidas.length)} em ${rotuloHoras(minutosEstimados)}.`}
          </p>
          <p>
            {`Tempo realizado: ${rotuloAtividades(escolhidas.length)} em ${rotuloHoras(minutosRealizados)}.`}
          </p>
        </div>
      </section>

      {carregando && <p className="registrar-ponto-estado" role="status">As atividades estão sendo buscadas.</p>}
      {erro && <p className="registrar-ponto-erro" role="alert">{erro}</p>}
      {!carregando && !erro && !ordenadas.length && (
        <p className="registrar-ponto-vazio">
          {embutido
            ? 'Nenhuma atividade em andamento para registrar.'
            : 'Nenhuma atividade com Natureza da atividade para registrar.'}
        </p>
      )}

      {!carregando && !erro && ordenadas.length > 0 && (
        <section className="registrar-ponto-principal">
          <MaterialReactTable
            columns={colunas}
            data={ordenadas}
            getRowId={row => String(row.atividade_id)}
            enableRowSelection
            enableColumnResizing
            columnResizeMode="onChange"
            layoutMode="grid"
            onRowSelectionChange={aoSelecionar}
            state={{ rowSelection: selecao }}
            enableColumnFilters={false}
            enableGlobalFilter={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableHiding={false}
            enableTopToolbar={false}
            localization={MRT_Localization_PT_BR}
            initialState={{ density: 'compact' }}
            defaultColumn={{ minSize: 64, maxSize: 720 }}
            muiTablePaperProps={{ elevation: 0, sx: { fontFamily: 'Exo, sans-serif', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } }}
            muiTableContainerProps={{ sx: { flex: 1, maxHeight: '100%' } }}
            muiTableHeadCellProps={{ sx: { fontFamily: 'Exo, sans-serif', fontSize: '12px', py: '4px', px: '8px' } }}
            muiTableBodyCellProps={{ sx: { fontFamily: 'Exo, sans-serif', fontSize: '12px', py: '2px', px: '8px' } }}
            muiBottomToolbarProps={{ sx: { minHeight: '36px' } }}
          />
        </section>
      )}

      <footer className="registrar-ponto-footer">
        <button type="button" onClick={registrar} disabled={gravando || carregando || !ordenadas.length}>
          {gravando ? 'Registrando...' : 'Registrar'}
        </button>
      </footer>
    </div>
  )
}
